import {
  SFTP_DIRECTORY_NAME_VALUES,
  type SftpDeliveryConfig,
} from '@steerfi/shared/types/jsonb/integration-profile.types';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { focusAfterRemoval } from '@/hooks/focus-after-removal';

interface SftpDeliveryFieldsProps {
  value: SftpDeliveryConfig | undefined;
  onChange: (v: SftpDeliveryConfig) => void;
  disabled?: boolean;
}

const emptyConfig = (): SftpDeliveryConfig => ({
  secretArn: '',
  directories: [],
});

export const SftpDeliveryFields: React.FC<SftpDeliveryFieldsProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const cfg = value ?? emptyConfig();
  const isDisabled = disabled ?? false;
  const dirIds = useRef<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  while (dirIds.current.length < cfg.directories.length) {
    dirIds.current.push(crypto.randomUUID());
  }

  const update = (patch: Partial<SftpDeliveryConfig>) => onChange({ ...cfg, ...patch });

  const addDirectory = () => {
    dirIds.current.push(crypto.randomUUID());
    update({ directories: [...cfg.directories, { name: 'outbound', path: '/outbound' }] });
  };

  const removeDirectory = (idx: number) => {
    focusAfterRemoval(rootRef.current, dirIds.current, idx, () => {
      dirIds.current.splice(idx, 1);
      update({ directories: cfg.directories.filter((_, i) => i !== idx) });
    });
  };

  const updateDirectory = (idx: number, field: 'name' | 'path', val: string) => {
    update({
      directories: cfg.directories.map((d, i) => (i === idx ? { ...d, [field]: val } : d)),
    });
  };

  return (
    <div ref={rootRef} className="space-y-4 rounded-xl border p-4">
      <div>
        <p className="text-sm font-medium text-foreground">Delivery (Outbound)</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          How payment files are pushed to the provider.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sftp-delivery-arn" className="text-xs">
          Secret reference <span className="text-destructive">*</span>
        </Label>
        <Input
          id="sftp-delivery-arn"
          className="text-xs font-mono"
          disabled={isDisabled}
          placeholder="arn:aws:secretsmanager:us-east-1:..."
          value={cfg.secretArn}
          onChange={(e) => update({ secretArn: e.target.value })}
        />
        <p className="text-[11px] text-muted-foreground">
          AWS Secrets Manager ARN containing connection credentials
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">
            Directories <span className="text-destructive">*</span>
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            disabled={isDisabled}
            data-add-row=""
            onClick={addDirectory}
          >
            <IconPlus size={12} />
            Add directory
          </Button>
        </div>
        {cfg.directories.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No directories configured.</p>
        )}
        {cfg.directories.length > 0 && (
          <div className="flex items-center gap-2 pl-5">
            <span className="w-32 shrink-0 text-[11px] text-muted-foreground">Name</span>
            <span className="flex-1 text-[11px] text-muted-foreground">
              Path <span className="text-destructive">*</span>
            </span>
          </div>
        )}
        <div className="space-y-1.5">
          {cfg.directories.map((dir, idx) => (
            <div key={dirIds.current[idx]} className="flex items-center gap-2">
              <Select
                disabled={isDisabled}
                value={
                  SFTP_DIRECTORY_NAME_VALUES.includes(
                    dir.name as (typeof SFTP_DIRECTORY_NAME_VALUES)[number]
                  )
                    ? dir.name
                    : '__custom__'
                }
                onValueChange={(v) =>
                  updateDirectory(idx, 'name', v === '__custom__' ? dir.name : v)
                }
              >
                <SelectTrigger className="h-7 text-xs w-32 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SFTP_DIRECTORY_NAME_VALUES.map((n) => (
                    <SelectItem key={n} value={n} className="text-xs">
                      {n.charAt(0).toUpperCase() + n.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="h-7 text-xs font-mono"
                disabled={isDisabled}
                placeholder="/path"
                value={dir.path}
                onChange={(e) => updateDirectory(idx, 'path', e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                disabled={isDisabled}
                aria-label={`Remove directory ${idx + 1}`}
                data-remove-row={dirIds.current[idx]}
                onClick={() => removeDirectory(idx)}
              >
                <IconTrash size={13} aria-hidden />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
